import React, { useState, useEffect, useRef } from "react";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../helpers/methods";
import {
  getProjectDetails,
  updateProjectDetail,
  uploadProductImage,
} from "../StepperService";
import { YhButton, YhInput } from "../../../../../ui";

interface CustomProps {
  nextStep?: () => void;
  userId: String;
  idd: string;
  data?: any;
  backStep?: () => void;
  handleClose: Function;
  fromSettingOrLogout?: any;
}

type MediaKind = "image" | "pdf" | "video";

type MediaField = {
  key: string;
  label: string;
  kind: MediaKind;
  accept: string;
  hint?: string;
};

const IMAGE_FIELDS = [
  "coverImage",
  "interiorImage",
  "exteriorImage",
  "masterPlan",
  "locationImage",
  "amenitiesImage",
  "pushCreative",
] as const;

const MOBILE_SIZE_HINT =
  "Mobile portrait · 3:4 ratio · 1200 × 1600 px recommended";

const mobileKey = (key: string) => `${key}Mobile`;

const MEDIA_FIELDS: MediaField[] = [
  {
    key: "coverImage",
    label: "Cover Image",
    kind: "image",
    accept: "image/*",
    hint: "Website / desktop · landscape",
  },
  {
    key: "interiorImage",
    label: "Interior Image",
    kind: "image",
    accept: "image/*",
  },
  {
    key: "exteriorImage",
    label: "Exterior Image",
    kind: "image",
    accept: "image/*",
  },
  {
    key: "masterPlan",
    label: "Master Plan Image",
    kind: "image",
    accept: "image/*",
  },
  {
    key: "locationImage",
    label: "Location Image",
    kind: "image",
    accept: "image/*",
  },
  {
    key: "amenitiesImage",
    label: "Amenities",
    kind: "image",
    accept: "image/*",
  },
  {
    key: "brochure",
    label: "Brochure",
    kind: "pdf",
    accept: "application/pdf,.pdf",
    hint: "Optional · PDF only. Hidden on the website until uploaded.",
  },
  {
    key: "videos",
    label: "Videos",
    kind: "video",
    accept: "video/*",
    hint: "MP4 / video files",
  },
];

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-2 flex items-center gap-1.5">
    <span className="text-sm font-semibold text-[#111827]">{children}</span>
    <span
      className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[#D1D5DB] text-[10px] text-[#9CA3AF]"
      title="More info"
    >
      i
    </span>
  </div>
);

const toUrlList = (value: any): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item;
        return item?.imageUrl || item?.url || item?.path || "";
      })
      .filter(Boolean);
  }
  if (typeof value === "string") return [value];
  return [];
};

const normalizeUploadPayload = (uploaded: any): string[] => {
  if (!uploaded) return [];
  if (typeof uploaded === "string") return [uploaded];
  if (Array.isArray(uploaded)) return toUrlList(uploaded);
  if (uploaded?.data) return normalizeUploadPayload(uploaded.data);
  if (uploaded?.imageUrl || uploaded?.url) {
    return [uploaded.imageUrl || uploaded.url];
  }
  return [];
};

const CommonUploader = ({
  label,
  hint,
  accept,
  kind,
  files = [],
  uploading,
  onUpload,
  onRemove,
  onPreview,
  onMakePrimary,
  single = false,
  showPrimary = true,
  previewClassName,
}: {
  label: string;
  hint?: string;
  accept: string;
  kind: MediaKind;
  files?: string[];
  uploading?: boolean;
  onUpload: (fileList: FileList | null) => void;
  onRemove: (index: number) => void;
  onPreview: (src: string) => void;
  onMakePrimary: (index: number) => void;
  single?: boolean;
  showPrimary?: boolean;
  previewClassName?: string;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="w-full">
      <FieldLabel>{label}</FieldLabel>
      <button
        type="button"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        className="flex w-full flex-col items-center justify-center rounded-2xl border border-dashed border-[#D4B8C0] bg-[#F8F9FF] px-4 py-8 text-center transition hover:border-[#A02448] hover:bg-[#EEF0FF] disabled:opacity-60"
      >
        <span className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-white text-2xl text-[#A02448] shadow-sm">
          ↑
        </span>
        <span className="text-sm font-semibold text-[#374151]">
          {uploading ? "Uploading..." : "Click or drag file to upload"}
        </span>
        {hint ? (
          <span className="mt-1 text-xs text-[#9CA3AF]">{hint}</span>
        ) : null}
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple={!single}
          accept={accept}
          onChange={(e) => {
            onUpload(e.target.files);
            e.target.value = "";
          }}
        />
      </button>

      {files.length > 0 ? (
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {files.map((src, index) => (
            <div
              key={`${src}-${index}`}
              className={`relative overflow-hidden rounded-xl border bg-white ${
                index === 0 ? "border-[#F59E0B]" : "border-[#EEF0F5]"
              }`}
            >
              {kind === "image" ? (
                <img
                  src={src}
                  alt={`${label} ${index + 1}`}
                  className={previewClassName || "h-44 w-full object-cover"}
                />
              ) : kind === "video" ? (
                <video
                  src={src}
                  className={
                    previewClassName || "h-44 w-full object-cover bg-black"
                  }
                  controls
                />
              ) : (
                <div className="flex h-44 flex-col items-center justify-center gap-2 bg-[#FEF2F2] text-[#DC2626]">
                  <span className="text-3xl font-bold">PDF</span>
                  <span className="max-w-[90%] truncate px-2 text-xs text-[#6B7280]">
                    {String(src).split("/").pop()}
                  </span>
                </div>
              )}

              <div className="absolute right-2 top-2 flex gap-1">
                <button
                  type="button"
                  title="Remove"
                  onClick={() => onRemove(index)}
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-xs text-white"
                >
                  ×
                </button>
                <button
                  type="button"
                  title="Preview"
                  onClick={() => onPreview(src)}
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-xs text-white"
                >
                  👁
                </button>
                {showPrimary ? (
                  <button
                    type="button"
                    title="Make primary"
                    onClick={() => onMakePrimary(index)}
                    className={`flex h-7 w-7 items-center justify-center rounded-full text-xs ${
                      index === 0
                        ? "bg-[#F59E0B] text-white"
                        : "bg-black/60 text-white"
                    }`}
                  >
                    ★
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const ProjectMedia = (props: CustomProps) => {
  const [mediaState, setMediaState] = useState<any>({
    coverImage: [],
    interiorImage: [],
    exteriorImage: [],
    masterPlan: [],
    locationImage: [],
    amenitiesImage: [],
    coverImageMobile: [],
    interiorImageMobile: [],
    exteriorImageMobile: [],
    masterPlanMobile: [],
    locationImageMobile: [],
    amenitiesImageMobile: [],
    pushCreativeMobile: [],
    useMobileMedia: false,
    brochure: [],
    videos: [],
    videoLinks: [""],
    project_name: "",
  });
  const [uploadingKey, setUploadingKey] = useState<string>("");

  useEffect(() => {
    projectDetails();
  }, [props?.idd]);

  const applyMedia = (project: any) => {
    if (!project) return;
    const projectImages = project?.projectImages || {};
    const brochure =
      projectImages?.brochure ||
      projectImages?.propertyBrochure ||
      project?.brochure ||
      project?.propertyBrochure ||
      [];
    const videoLinks = toUrlList(projectImages?.videoLinks);
    setMediaState({
      coverImage: toUrlList(projectImages.coverImage),
      interiorImage: toUrlList(projectImages.interiorImage),
      exteriorImage: toUrlList(projectImages.exteriorImage),
      masterPlan: toUrlList(projectImages.masterPlan),
      locationImage: toUrlList(projectImages.locationImage),
      amenitiesImage: toUrlList(projectImages.amenitiesImage),
      pushCreative: toUrlList(projectImages.pushCreative),
      coverImageMobile: toUrlList(projectImages.coverImageMobile),
      interiorImageMobile: toUrlList(projectImages.interiorImageMobile),
      exteriorImageMobile: toUrlList(projectImages.exteriorImageMobile),
      masterPlanMobile: toUrlList(projectImages.masterPlanMobile),
      locationImageMobile: toUrlList(projectImages.locationImageMobile),
      amenitiesImageMobile: toUrlList(projectImages.amenitiesImageMobile),
      pushCreativeMobile: toUrlList(projectImages.pushCreativeMobile),
      useMobileMedia: Boolean(projectImages.useMobileMedia),
      brochure: toUrlList(brochure),
      videos: toUrlList(projectImages.videos),
      videoLinks: videoLinks.length ? videoLinks : [""],
      project_name: project?.project_name || "",
    });
  };

  const projectDetails = async () => {
    if (props?.data) applyMedia(props.data);
    if (!props?.idd) return;
    try {
      const response = await getProjectDetails(props.idd);
      if (response?.data) applyMedia(response.data);
    } catch (error: any) {
      if (!props?.data) {
        openErrorNotification(
          typeof error?.message === "string"
            ? error.message
            : "Failed to load project media"
        );
      }
    }
  };

  const makePrimaryImage = (key: string, index: number) => {
    setMediaState((prev: any) => {
      const updated = [...(prev[key] || [])];
      if (index <= 0 || !updated[index]) return prev;
      const [selected] = updated.splice(index, 1);
      updated.unshift(selected);
      return { ...prev, [key]: updated };
    });
  };

  const handleFileChange = async (files: FileList | null, key: string) => {
    if (!files?.length) return;
    let fileArr = Array.from(files);
    if (key === "brochure") {
      const pdf = fileArr.find(
        (file) =>
          file.type === "application/pdf" || /\.pdf$/i.test(file.name)
      );
      if (!pdf) {
        openErrorNotification("Please upload a PDF brochure");
        return;
      }
      fileArr = [pdf];
    }
    const uploadedUrls: string[] = [];
    setUploadingKey(key);

    for (const file of fileArr) {
      const data = new FormData();
      data.append("files", file);
      try {
        const uploaded = await uploadProductImage(
          data,
          mediaState.project_name || "project"
        );
        uploadedUrls.push(...normalizeUploadPayload(uploaded));
      } catch {
        openErrorNotification("Upload failed");
      }
    }

    if (uploadedUrls.length) {
      setMediaState((prev: any) => ({
        ...prev,
        [key]:
          key === "brochure"
            ? uploadedUrls.slice(0, 1)
            : [...(prev[key] || []), ...uploadedUrls],
      }));
    }
    setUploadingKey("");
  };

  const removeImage = (key: string, index: number) => {
    setMediaState((prev: any) => {
      const updated = [...(prev[key] || [])];
      updated.splice(index, 1);
      return { ...prev, [key]: updated };
    });
  };

  const handleVideoLinkChange = (index: number, value: string) => {
    setMediaState((prev: any) => {
      const links = [...(prev.videoLinks || [])];
      links[index] = value;
      return { ...prev, videoLinks: links };
    });
  };

  const addVideoLink = () => {
    setMediaState((prev: any) => ({
      ...prev,
      videoLinks: [...(prev.videoLinks || []), ""],
    }));
  };

  const removeVideoLink = (index: number) => {
    setMediaState((prev: any) => {
      const links = [...(prev.videoLinks || [])];
      links.splice(index, 1);
      return { ...prev, videoLinks: links.length ? links : [""] };
    });
  };

  const submitForm = async () => {
    try {
      if (!props?.idd) {
        openErrorNotification("Please save project information first");
        return;
      }

      const brochureFiles = mediaState.brochure || [];
      const videoLinks = (mediaState.videoLinks || [])
        .map((v: string) => String(v || "").trim())
        .filter(Boolean);

      const projectImages = {
        coverImage: mediaState.coverImage || [],
        interiorImage: mediaState.interiorImage || [],
        exteriorImage: mediaState.exteriorImage || [],
        masterPlan: mediaState.masterPlan || [],
        locationImage: mediaState.locationImage || [],
        amenitiesImage: mediaState.amenitiesImage || [],
        pushCreative: mediaState.pushCreative || [],
        useMobileMedia: Boolean(mediaState.useMobileMedia),
        coverImageMobile: mediaState.coverImageMobile || [],
        interiorImageMobile: mediaState.interiorImageMobile || [],
        exteriorImageMobile: mediaState.exteriorImageMobile || [],
        masterPlanMobile: mediaState.masterPlanMobile || [],
        locationImageMobile: mediaState.locationImageMobile || [],
        amenitiesImageMobile: mediaState.amenitiesImageMobile || [],
        pushCreativeMobile: mediaState.pushCreativeMobile || [],
        brochure: brochureFiles,
        videos: mediaState.videos || [],
        videoLinks,
      };

      const payload = {
        _id: props.idd,
        projectImages,
        propertyBrochure: brochureFiles,
      };

      const response = await updateProjectDetail(payload);
      if (response?.success) {
        openSuccessNotification(response?.message);
        props.nextStep?.();
      }
    } catch (error: any) {
      openErrorNotification(error?.message || "Failed to save project media");
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6 flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EEF0FF] text-sm font-bold text-[#A02448]">
          ☰
        </span>
        <div>
          <h2 className="text-lg font-bold tracking-tight text-[#111827]">
            Project Media
          </h2>
          <p className="mt-0.5 text-sm text-[#6B7280]">
            Kindly confirm some facts about the property
          </p>
        </div>
      </div>

      <div className="mb-5 rounded-2xl border border-[#EEF0F5] bg-[#FAFBFF] p-4 sm:p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-bold text-[#111827]">
              Mobile media
            </h3>
            <p className="mt-1 text-sm text-[#6B7280]">
              Off by default. Turn on to upload a separate portrait set for
              phones. Recommended size{" "}
              <span className="font-semibold text-[#111827]">
                3:4 (1200 × 1600 px)
              </span>
              . Desktop and tablet keep using the website images below.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={Boolean(mediaState.useMobileMedia)}
            onClick={() =>
              setMediaState((prev: any) => ({
                ...prev,
                useMobileMedia: !prev.useMobileMedia,
              }))
            }
            className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition ${
              mediaState.useMobileMedia ? "bg-[#841C39]" : "bg-[#D1D5DB]"
            }`}
          >
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${
                mediaState.useMobileMedia ? "left-[22px]" : "left-0.5"
              }`}
            />
          </button>
        </div>
      </div>

      <div className="space-y-5">
        <div className="rounded-2xl border border-[#F3D5DE] bg-[#FFF8F8] p-4 sm:p-5">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-bold text-[#111827]">Brochure</h3>
              <p className="mt-1 text-sm text-[#6B7280]">
                Optional. Upload a PDF to show Download Brochure on the project
                page. Remove it and the brochure section stays hidden.
              </p>
            </div>
            <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-[#A02448]">
              Optional
            </span>
          </div>
          <CommonUploader
            label="Brochure PDF"
            hint="Optional · one PDF. Hidden on the website until uploaded."
            accept="application/pdf,.pdf"
            kind="pdf"
            single
            showPrimary={false}
            files={toUrlList(mediaState.brochure)}
            uploading={uploadingKey === "brochure"}
            onUpload={(files) => handleFileChange(files, "brochure")}
            onRemove={(index) => removeImage("brochure", index)}
            onPreview={(src) => window.open(src, "_blank")}
            onMakePrimary={() => undefined}
          />
        </div>

        {MEDIA_FIELDS.filter((field) => field.key !== "brochure").map((field) => (
          <div
            key={field.key}
            className="rounded-2xl border border-[#EEF0F5] bg-[#FAFBFF] p-4 sm:p-5"
          >
            <CommonUploader
              label={field.label}
              hint={field.hint}
              accept={field.accept}
              kind={field.kind}
              files={toUrlList(mediaState[field.key])}
              uploading={uploadingKey === field.key}
              onUpload={(files) => handleFileChange(files, field.key)}
              onRemove={(index) => removeImage(field.key, index)}
              onPreview={(src) => window.open(src, "_blank")}
              onMakePrimary={(index) => makePrimaryImage(field.key, index)}
            />
            {mediaState.useMobileMedia &&
            IMAGE_FIELDS.includes(field.key as (typeof IMAGE_FIELDS)[number]) ? (
              <div className="mt-5 border-t border-dashed border-[#E5E7EB] pt-5">
                <CommonUploader
                  label={`Mobile ${field.label}`}
                  hint={MOBILE_SIZE_HINT}
                  accept={field.accept}
                  kind={field.kind}
                  files={toUrlList(mediaState[mobileKey(field.key)])}
                  uploading={uploadingKey === mobileKey(field.key)}
                  previewClassName="aspect-[3/4] h-auto w-full object-cover"
                  onUpload={(files) =>
                    handleFileChange(files, mobileKey(field.key))
                  }
                  onRemove={(index) =>
                    removeImage(mobileKey(field.key), index)
                  }
                  onPreview={(src) => window.open(src, "_blank")}
                  onMakePrimary={(index) =>
                    makePrimaryImage(mobileKey(field.key), index)
                  }
                />
              </div>
            ) : null}
          </div>
        ))}

        <div className="rounded-2xl border border-[#EEF0F5] bg-[#FAFBFF] p-4 sm:p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <FieldLabel>Video Links</FieldLabel>
            <YhButton variant="secondary" size="sm" onClick={addVideoLink}>
              + Add Link
            </YhButton>
          </div>
          <div className="space-y-3">
            {(mediaState.videoLinks || [""]).map(
              (link: string, index: number) => (
                <div
                  key={index}
                  className="grid grid-cols-1 items-end gap-2 sm:grid-cols-[1fr_auto]"
                >
                  <YhInput
                    placeholder="https://youtube.com/..."
                    value={link}
                    onChange={(e) =>
                      handleVideoLinkChange(index, e.target.value)
                    }
                  />
                  {(mediaState.videoLinks || []).length > 1 ? (
                    <button
                      type="button"
                      onClick={() => removeVideoLink(index)}
                      className="mb-1 flex h-10 w-10 items-center justify-center rounded-lg text-[#DC2626] transition hover:bg-[#FEF2F2]"
                      aria-label="Remove link"
                    >
                      ×
                    </button>
                  ) : null}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between gap-3 border-t border-[#EEF0F5] pt-5">
        <YhButton variant="secondary" onClick={() => props.backStep?.()}>
          ← Back
        </YhButton>
        <YhButton variant="primary" onClick={submitForm}>
          Save & Continue →
        </YhButton>
      </div>
    </div>
  );
};

export default ProjectMedia;
